/**
 * ImportPropertiesModal — wizard de 5 pasos para importar/exportar propiedades
 * vía Excel. Backend: /api/properties/import/* y /api/properties/export.
 *
 * Pasos:
 *   0 → Punto de entrada (subir / actualizar / plantilla / exportar)
 *   1 → Subida del archivo
 *   2 → Reconciliación de columnas (skip si todo OK)
 *   3 → Revisión financiera (outliers + responsabilidad)
 *   4 → Resumen final + ejecución
 *   5 → Resultado (success / error)
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { APP_NAME } from '@/lib/brand';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Upload, FileSpreadsheet, FileDown, Pencil, Loader2, CheckCircle2, XCircle,
  AlertTriangle, ArrowLeft, ArrowRight, Sparkles, RefreshCcw, ClipboardList, X,
} from 'lucide-react';

// ── Constantes UI ──────────────────────────────────────────────────────
const TEMPLATE_GROUPS = [
  { title: 'Ubicación', fields: ['address', 'zone', 'city', 'state', 'zip_code'] },
  { title: 'Dimensiones', fields: ['area_m2', 'bedrooms', 'bathrooms', 'half_bathrooms', 'parking_spots'] },
  { title: 'Financiero', fields: ['commission_pct', 'extra_commission_pct'] },
  { title: 'Extra', fields: ['description', 'status', 'google_maps_url', 'group_name'] },
  { title: 'Multimedia y amenidades', fields: ['images', 'amenities', 'characteristics'] },
];

const FIELD_LABELS_ES = {
  title: 'Título', description: 'Descripción', property_type: 'Tipo de propiedad',
  operation_type: 'Tipo de operación', price: 'Precio', currency: 'Moneda',
  area_m2: 'Área (m²)', bedrooms: 'Recámaras', bathrooms: 'Baños',
  half_bathrooms: 'Medios baños', parking_spots: 'Estacionamientos',
  address: 'Dirección', zone: 'Zona', city: 'Ciudad', state: 'Estado',
  zip_code: 'Código postal', commission_pct: 'Comisión (%)',
  extra_commission_pct: 'Comisión extra (%)', status: 'Estado',
  property_id: 'ID propiedad',
  google_maps_url: 'URL Google Maps',
  group_name: 'Grupo de propiedades',
  images: 'Imágenes (URLs)',
  amenities: 'Amenidades',
  characteristics: 'Características',
};

const REQUIRED_FIELDS = ['title', 'price', 'currency', 'property_type'];

// ── Helpers ────────────────────────────────────────────────────────────
const fmtPrice = (value, currency = 'MXN') => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) return '-';
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(Number(value));
  } catch {
    return `$${Number(value).toLocaleString('es-MX')} ${currency}`;
  }
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const buildResultsCsv = (rows) => {
  const headers = ['fila', 'property_id', 'estado', 'detalle'];
  const lines = [headers.join(',')];
  for (const r of rows || []) {
    const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    lines.push([r.row_index, r.property_id || '', r.status, r.error || ''].map(cell).join(','));
  }
  return lines.join('\n');
};

// ── Componente principal ───────────────────────────────────────────────
export const ImportPropertiesModal = ({ open, onClose, onComplete }) => {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('create'); // 'create' | 'update'
  const [file, setFile] = useState(null);
  const [parseRes, setParseRes] = useState(null); // respuesta del /parse
  const [executing, setExecuting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Step 2 (reconciliación)
  const [missingAcknowledged, setMissingAcknowledged] = useState(false);

  // Step 3 (revisión financiera)
  const [confirmedOutliers, setConfirmedOutliers] = useState(new Set());
  const [responsibilityAccepted, setResponsibilityAccepted] = useState(false);

  // Step 5 (resultado)
  const [executeRes, setExecuteRes] = useState(null);

  // Constructor de plantilla (flujo C)
  const [templateLoading, setTemplateLoading] = useState(false);

  // Panel de historial de importaciones
  const [showLogs, setShowLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsItems, setLogsItems] = useState([]);

  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // ── Cargar historial cuando se abre el panel ──
  useEffect(() => {
    if (!showLogs) return;
    let cancelled = false;
    (async () => {
      setLogsLoading(true);
      try {
        const r = await api.get('/properties/import/logs');
        if (!cancelled) setLogsItems(r.data?.items || []);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.detail || 'Error al cargar historial');
          setLogsItems([]);
        }
      } finally {
        if (!cancelled) setLogsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showLogs]);

  // ── Reset al cerrar ──
  const handleClose = () => {
    setStep(0);
    setMode('create');
    setFile(null);
    setParseRes(null);
    setExecuteRes(null);
    setMissingAcknowledged(false);
    setConfirmedOutliers(new Set());
    setResponsibilityAccepted(false);
    setShowLogs(false);
    onClose?.();
  };

  // ── Step 0: handlers ──
  const handleStartCreate = () => { setMode('create'); setStep(1); };
  const handleStartUpdate = () => { setMode('update'); setStep(1); };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const r = await api.get('/properties/export', { responseType: 'blob' });
      const fname = `propiedades_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.xlsx`;
      downloadBlob(r.data, fname);
      toast.success('Exportación descargada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al exportar');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      // Sin parámetro `fields` → backend devuelve plantilla completa.
      const r = await api.get('/properties/import/template', {
        responseType: 'blob',
      });
      downloadBlob(r.data, 'plantilla_propiedades.xlsx');
      toast.success('Plantilla descargada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al descargar plantilla');
    } finally {
      setTemplateLoading(false);
    }
  };

  // ── Step 1: subir archivo ──
  const handleFileSelected = async (selected) => {
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.xlsx') && !selected.name.toLowerCase().endsWith('.xlsm')) {
      toast.error('Solo se aceptan archivos .xlsx');
      return;
    }
    setFile(selected);
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', selected);
      const r = await api.post('/properties/import/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParseRes(r.data);
      // Avanza directo: si no hay missing/unknown → step 3, si hay → step 2
      const needsReconcile = (r.data.missing_fields?.length || 0) > 0 || (r.data.unknown_columns?.length || 0) > 0;
      setStep(needsReconcile ? 2 : 3);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al analizar el archivo');
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFileSelected(f);
  };

  // ── Step 3: outliers ──
  const toggleOutlier = (rowIdx) => {
    setConfirmedOutliers((prev) => {
      const next = new Set(prev);
      if (next.has(rowIdx)) next.delete(rowIdx); else next.add(rowIdx);
      return next;
    });
  };

  const allOutliersConfirmed = useMemo(() => {
    if (!parseRes?.outlier_rows) return true;
    return parseRes.outlier_rows.every((o) => confirmedOutliers.has(o.row));
  }, [parseRes, confirmedOutliers]);

  const canProceedStep3 = allOutliersConfirmed && responsibilityAccepted;

  // ── Step 4: ejecutar ──
  const handleExecute = async () => {
    if (!parseRes) return;
    setExecuting(true);
    try {
      const r = await api.post('/properties/import/execute', {
        session_id: parseRes.session_id,
        confirmed_outliers: Array.from(confirmedOutliers),
        skip_missing_fields: missingAcknowledged,
        responsibility_accepted: true,
      });
      setExecuteRes(r.data);
      setStep(5);
    } catch (e) {
      setExecuteRes({ success: false, error: e.response?.data?.detail || 'Error al ejecutar la importación' });
      setStep(5);
    } finally {
      setExecuting(false);
    }
  };

  // ── Step 5: cerrar y recargar ──
  const handleFinishAndReload = () => {
    onComplete?.();
    handleClose();
  };

  const handleDownloadReport = async () => {
    try {
      // Pedimos el log al backend para tener row-by-row detail
      const r = await api.get('/properties/import/logs');
      const log = (r.data.items || []).find((l) => l.id === executeRes?.log_id);
      const rows = log?.rows || [];
      const csv = buildResultsCsv(rows);
      downloadBlob(new Blob([csv], { type: 'text/csv' }), `reporte_import_${Date.now()}.csv`);
    } catch {
      toast.error('No se pudo generar el reporte');
    }
  };

  const handleRetry = () => {
    setStep(0);
    setFile(null);
    setParseRes(null);
    setExecuteRes(null);
    setMissingAcknowledged(false);
    setConfirmedOutliers(new Set());
    setResponsibilityAccepted(false);
  };

  // ── Render por paso ──
  const renderLogsPanel = () => {
    const fmtDate = (iso) => {
      if (!iso) return '-';
      try {
        return new Intl.DateTimeFormat('es-MX', {
          dateStyle: 'medium', timeStyle: 'short',
        }).format(new Date(iso));
      } catch {
        return iso;
      }
    };
    const fmtMoney = (n, cur = 'MXN') => {
      if (n === null || n === undefined || isNaN(Number(n))) return '-';
      try {
        return new Intl.NumberFormat('es-MX', {
          style: 'currency', currency: cur, maximumFractionDigits: 0,
        }).format(Number(n));
      } catch {
        return `$${Number(n).toLocaleString('es-MX')} ${cur}`;
      }
    };

    return (
      <div className="space-y-3" data-testid="import-logs-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-purple-400" />
            <h3 className="text-sm font-semibold text-white">Historial de importaciones</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLogs(false)}
            className="text-zinc-400 hover:text-white"
            data-testid="import-logs-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {logsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
          </div>
        ) : logsItems.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-black/20 p-8 text-center">
            <p className="text-sm text-muted-foreground">Aún no hay importaciones registradas</p>
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="import-logs-table">
                <thead className="bg-black/40 sticky top-0">
                  <tr className="text-left text-muted-foreground">
                    <th className="p-2">Fecha</th>
                    <th className="p-2">Usuario</th>
                    <th className="p-2">Archivo</th>
                    <th className="p-2">Modo</th>
                    <th className="p-2 text-right">Importadas</th>
                    <th className="p-2 text-right">Actualizadas</th>
                    <th className="p-2 text-right">Fallidas</th>
                    <th className="p-2 text-right">Valor total</th>
                  </tr>
                </thead>
                <tbody>
                  {logsItems.map((log) => {
                    const fin = log.financial_summary || {};
                    const cur = (fin.currencies_used && fin.currencies_used[0]) || 'MXN';
                    const isCreate = log.mode === 'create';
                    return (
                      <tr
                        key={log.id}
                        className="border-t border-white/5 hover:bg-white/5"
                        data-testid={`import-log-row-${log.id}`}
                      >
                        <td className="p-2 text-zinc-300 whitespace-nowrap">{fmtDate(log.completed_at || log.uploaded_at)}</td>
                        <td className="p-2 text-zinc-400">{log.user_email || '-'}</td>
                        <td className="p-2 text-white truncate max-w-[180px]" title={log.filename}>{log.filename}</td>
                        <td className="p-2">
                          <Badge
                            className={isCreate
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px]'
                              : 'bg-blue-500/20 text-blue-300 border border-blue-500/30 text-[10px]'}
                          >
                            {isCreate ? 'Nueva' : 'Actualización'}
                          </Badge>
                        </td>
                        <td className="p-2 text-right text-emerald-300 font-semibold">{log.imported || 0}</td>
                        <td className="p-2 text-right text-blue-300 font-semibold">{log.updated || 0}</td>
                        <td className={`p-2 text-right font-semibold ${(log.failed || 0) > 0 ? 'text-red-300' : 'text-zinc-400'}`}>{log.failed || 0}</td>
                        <td className="p-2 text-right text-amber-300 whitespace-nowrap">{fmtMoney(fin.total_value, cur)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStep0 = () => (
    <div className="space-y-4" data-testid="import-step-0">
      <div className="grid sm:grid-cols-2 gap-3">
        <Card
          onClick={handleStartCreate}
          className="cursor-pointer hover:border-primary/40 bg-black/20 border-white/10 transition"
          data-testid="import-option-create"
        >
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-400" />
              <span className="text-sm font-semibold text-white">Subir Excel</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Crea propiedades nuevas desde un archivo .xlsx con tus datos.
            </p>
          </CardContent>
        </Card>

        <Card
          onClick={handleStartUpdate}
          className="cursor-pointer hover:border-amber-400/40 bg-black/20 border-white/10 transition"
          data-testid="import-option-update"
        >
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-amber-400" />
              <span className="text-sm font-semibold text-white">Actualizar existentes</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Modifica propiedades ya creadas usando la columna <code>property_id</code>.
            </p>
          </CardContent>
        </Card>

        <Card
          onClick={handleDownloadTemplate}
          className="cursor-pointer hover:border-blue-400/40 bg-black/20 border-white/10 transition"
          data-testid="import-option-template"
        >
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              {templateLoading ? <Loader2 className="w-5 h-5 text-blue-400 animate-spin" /> : <FileSpreadsheet className="w-5 h-5 text-blue-400" />}
              <span className="text-sm font-semibold text-white">Descargar plantilla</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Genera un Excel vacío con los campos que necesitas llenar.
            </p>
            <div className="mt-2 space-y-1">
              <p className="text-[11px] text-amber-300 font-medium">
                Campos obligatorios: Título, Precio, Moneda, Tipo de propiedad
              </p>
              <p className="text-[10px] text-muted-foreground">
                La plantilla incluye todos los campos disponibles.
                Las columnas vacías serán ignoradas al importar.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card
          onClick={handleExport}
          className="cursor-pointer hover:border-zinc-400/40 bg-black/20 border-white/10 transition"
          data-testid="import-option-export"
        >
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              {exportLoading ? <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" /> : <FileDown className="w-5 h-5 text-zinc-400" />}
              <span className="text-sm font-semibold text-white">Exportar mis propiedades</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Descarga todas las propiedades de tu empresa en formato Excel.
            </p>
          </CardContent>
        </Card>

        <Card
          onClick={() => setShowLogs(true)}
          className="cursor-pointer hover:border-purple-400/40 bg-black/20 border-white/10 transition col-span-2"
          data-testid="import-option-logs"
        >
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-400" />
              <span className="text-sm font-semibold text-white">Historial de importaciones</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Consulta quién importó o exportó propiedades y cuándo.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4" data-testid="import-step-1">
      {mode === 'update' && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2" data-testid="import-update-banner">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-300">
            Primero exporta tus propiedades, edita el Excel y vuelve a importarlo
            con la columna <code>property_id</code> intacta.
          </p>
        </div>
      )}

      <div
        ref={dropRef}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        className="rounded-xl border-2 border-dashed border-white/20 bg-black/20 p-8 text-center cursor-pointer hover:border-primary/40 transition"
        data-testid="import-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xlsm"
          onChange={(e) => handleFileSelected(e.target.files?.[0])}
          className="hidden"
          data-testid="import-file-input"
        />
        {parsing ? (
          <div className="space-y-2">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-400" />
            <p className="text-sm text-white">Analizando archivo...</p>
            <p className="text-[11px] text-muted-foreground">{file?.name}</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-white">Arrastra tu archivo aquí o haz clic para seleccionar</p>
            <p className="text-[11px] text-muted-foreground">Solo formato .xlsx (máx 10 MB)</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => {
    const missing = parseRes?.missing_fields || [];
    const unknown = parseRes?.unknown_columns || [];
    const canContinue = missing.length === 0 || missingAcknowledged;

    return (
      <div className="space-y-4" data-testid="import-step-2">
        {missing.length > 0 && (
          <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 space-y-2" data-testid="import-missing-fields">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <p className="text-sm font-semibold text-white">Campos no encontrados en tu archivo</p>
            </div>
            <p className="text-[11px] text-muted-foreground">Estas propiedades quedarán sin estos datos:</p>
            <div className="flex flex-wrap gap-1.5">
              {missing.map((f) => (
                <Badge key={f} className="bg-red-500/10 text-red-300 border border-red-500/30" data-testid={`import-missing-badge-${f}`}>
                  {FIELD_LABELS_ES[f] || f}
                </Badge>
              ))}
            </div>
            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="import-missing-ack">
              <Checkbox
                checked={missingAcknowledged}
                onCheckedChange={(v) => setMissingAcknowledged(Boolean(v))}
              />
              <span className="text-xs text-zinc-300">Entiendo que estos campos quedarán vacíos</span>
            </label>
          </div>
        )}

        {unknown.length > 0 && (
          <div className="rounded-xl border border-zinc-500/20 bg-zinc-500/5 p-4 space-y-2" data-testid="import-unknown-columns">
            <p className="text-sm font-semibold text-white">Columnas no reconocidas</p>
            <p className="text-[11px] text-muted-foreground">Estas columnas serán ignoradas:</p>
            <div className="flex flex-wrap gap-1.5">
              {unknown.map((c) => (
                <Badge key={c} className="bg-zinc-500/10 text-zinc-300 border border-zinc-500/30">
                  {c}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={() => setStep(1)} data-testid="import-step-2-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <Button
            onClick={() => setStep(3)}
            disabled={!canContinue}
            className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
            data-testid="import-step-2-continue"
          >
            Continuar <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    // Combina filas del preview con filas que SOLO existen en outlier_rows
    // (necesario cuando hay >5 filas y el outlier cae fuera del preview).
    // Sin esto el botón "Confirmar y continuar" quedaba bloqueado porque el
    // usuario no tenía UI para marcar checkboxes de outliers no visibles.
    const previewRows = parseRes?.preview || [];
    const previewRowIndices = new Set(previewRows.map((r) => r.row));
    const extraOutlierRows = (parseRes?.outlier_rows || [])
      .filter((o) => !previewRowIndices.has(o.row))
      .map((o) => ({
        row: o.row,
        data: { title: `Fila ${o.row}`, price: o.price, currency: 'MXN' },
        _isExtraOutlier: true,
      }));
    const allRows = [...previewRows, ...extraOutlierRows];
    const errorRows = parseRes?.error_rows || [];
    const outlierMap = new Map((parseRes?.outlier_rows || []).map((o) => [o.row, o]));

    // Para mostrar todas las filas válidas: usamos preview (5) — para más detalle el backend
    // ya nos devuelve resúmenes. Aquí mostramos lo que tenemos.
    return (
      <div className="space-y-3" data-testid="import-step-3">
        <div className="grid grid-cols-3 gap-2">
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Total de propiedades</p>
              <p className="text-xl font-bold text-white">{parseRes?.valid_rows || 0}</p>
            </CardContent>
          </Card>
          <Card className={`border-white/10 ${(parseRes?.outlier_rows?.length || 0) > 0 ? 'bg-amber-500/10' : 'bg-black/20'}`}>
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Con alertas de precio</p>
              <p className={`text-xl font-bold ${(parseRes?.outlier_rows?.length || 0) > 0 ? 'text-amber-300' : 'text-white'}`}>
                {parseRes?.outlier_rows?.length || 0}
              </p>
            </CardContent>
          </Card>
          <Card className={`border-white/10 ${errorRows.length > 0 ? 'bg-red-500/10' : 'bg-black/20'}`}>
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Errores (excluidas)</p>
              <p className={`text-xl font-bold ${errorRows.length > 0 ? 'text-red-300' : 'text-white'}`}>{errorRows.length}</p>
            </CardContent>
          </Card>
        </div>

        <div className="rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="import-step-3-table">
              <thead className="bg-black/40 sticky top-0">
                <tr className="text-left text-muted-foreground">
                  <th className="p-2">Fila</th>
                  <th className="p-2">Nombre</th>
                  <th className="p-2">Precio</th>
                  <th className="p-2">Moneda</th>
                  <th className="p-2">Comisión</th>
                  <th className="p-2">Estado</th>
                  <th className="p-2">Acción</th>
                </tr>
              </thead>
              <tbody>
                {allRows.map((rowEntry) => {
                  const { row, data, _isExtraOutlier } = rowEntry;
                  const outlier = outlierMap.get(row);
                  const rowClass = outlier ? 'bg-amber-500/5' : 'hover:bg-white/5';
                  return (
                    <tr key={`r-${row}`} className={`border-t border-white/5 ${rowClass}`} data-testid={`import-row-${row}`}>
                      <td className="p-2 text-muted-foreground">{row}</td>
                      <td className={`p-2 ${_isExtraOutlier ? 'text-zinc-500 italic' : 'text-white'}`}>{data.title || '-'}</td>
                      <td className="p-2 text-white">{_isExtraOutlier ? data.price : fmtPrice(data.price, data.currency)}</td>
                      <td className="p-2 text-zinc-400">{data.currency || 'MXN'}</td>
                      <td className="p-2 text-zinc-400">{data.commission_pct != null ? `${data.commission_pct}%` : '-'}</td>
                      <td className="p-2">
                        {outlier && (
                          <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px]" data-testid={`outlier-badge-${row}`}>
                            <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Precio inusual
                          </Badge>
                        )}
                      </td>
                      <td className="p-2">
                        {outlier && (
                          <label className="flex items-center gap-1 text-[11px] cursor-pointer">
                            <Checkbox
                              checked={confirmedOutliers.has(row)}
                              onCheckedChange={() => toggleOutlier(row)}
                              data-testid={`outlier-confirm-${row}`}
                            />
                            <span className="text-amber-300">Confirmo correcto</span>
                          </label>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {errorRows.map((er) => (
                  <tr key={`e-${er.row}`} className="border-t border-white/5 bg-red-500/5" data-testid={`import-error-row-${er.row}`}>
                    <td className="p-2 text-muted-foreground">{er.row}</td>
                    <td className="p-2 text-red-300" colSpan={5}>
                      <div className="flex items-center gap-1">
                        <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 text-[10px]">
                          <XCircle className="w-2.5 h-2.5 mr-0.5" />Error
                        </Badge>
                        <span className="text-[11px] text-red-300">{er.error}</span>
                      </div>
                    </td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {parseRes?.valid_rows > allRows.length && (
            <p className="p-2 text-[10px] text-muted-foreground text-center bg-black/30">
              Mostrando preview de {allRows.length} de {parseRes?.valid_rows} filas. La importación procesa todas.
            </p>
          )}
        </div>

        <label
          className="flex items-start gap-2 p-3 rounded-lg bg-black/30 border border-white/10 cursor-pointer"
          data-testid="import-responsibility-checkbox"
        >
          <Checkbox
            checked={responsibilityAccepted}
            onCheckedChange={(v) => setResponsibilityAccepted(Boolean(v))}
            className="mt-0.5"
          />
          <span className="text-[11px] text-zinc-300 leading-relaxed">
            Confirmo que los precios, monedas y comisiones mostrados son correctos
            y autorizo su publicación. Entiendo que {APP_NAME} no es responsable
            de errores en los datos proporcionados.
          </span>
        </label>

        <div className="flex justify-between pt-1">
          <Button variant="ghost" onClick={() => setStep(parseRes?.missing_fields?.length || parseRes?.unknown_columns?.length ? 2 : 1)} data-testid="import-step-3-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <Button
            onClick={() => setStep(4)}
            disabled={!canProceedStep3}
            className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
            data-testid="import-step-3-continue"
          >
            Confirmar y continuar <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    const fin = parseRes?.financial_summary || {};
    const totalValue = fin.total_value || 0;
    const minPrice = fin.price_min || 0;
    const maxPrice = fin.price_max || 0;
    const currencies = fin.currencies_used || ['MXN'];
    const action = mode === 'create' ? 'crear' : 'actualizar';

    return (
      <div className="space-y-4" data-testid="import-step-4">
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-white">Listo para importar</p>
            <p className="text-[11px] text-muted-foreground">
              Se {action === 'crear' ? 'crearán' : 'actualizarán'} {parseRes?.valid_rows || 0} propiedades en {APP_NAME}.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">A {action}</p>
              <p className="text-xl font-bold text-white">{parseRes?.valid_rows || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Valor total del portafolio</p>
              <p className="text-xl font-bold text-white">{fmtPrice(totalValue, currencies[0])}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Rango precios</p>
              <p className="text-xs font-semibold text-white">{fmtPrice(minPrice, currencies[0])} – {fmtPrice(maxPrice, currencies[0])}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Errores excluidos</p>
              <p className={`text-xl font-bold ${(parseRes?.error_rows?.length || 0) > 0 ? 'text-red-300' : 'text-white'}`}>
                {parseRes?.error_rows?.length || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Monedas</p>
              <p className="text-xs font-semibold text-white">{currencies.join(', ')}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Outliers confirmados</p>
              <p className="text-xl font-bold text-white">{confirmedOutliers.size} / {parseRes?.outlier_rows?.length || 0}</p>
            </CardContent>
          </Card>
        </div>

        {executing && (
          <div className="rounded-xl border border-emerald-500/20 bg-black/30 p-4 space-y-2" data-testid="import-progress">
            <div className="h-2 rounded-full bg-emerald-500/10 overflow-hidden">
              <div className="h-full bg-emerald-500/60 animate-pulse" style={{ width: '60%' }} />
            </div>
            <p className="text-xs text-emerald-300 text-center flex items-center justify-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Importando...
            </p>
          </div>
        )}

        <div className="flex justify-between pt-1">
          <Button variant="ghost" onClick={() => setStep(3)} disabled={executing} data-testid="import-step-4-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <Button
            onClick={handleExecute}
            disabled={executing}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="import-execute-btn"
          >
            {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Importar ahora
          </Button>
        </div>
      </div>
    );
  };

  const renderStep5 = () => {
    if (!executeRes) return null;
    if (executeRes.success === false || executeRes.error) {
      return (
        <div className="space-y-4 text-center" data-testid="import-step-5-error">
          <XCircle className="w-12 h-12 mx-auto text-red-400" />
          <p className="text-base font-semibold text-white">Error en la importación</p>
          <p className="text-sm text-muted-foreground">{executeRes.error || 'Ocurrió un error desconocido.'}</p>
          <div className="flex justify-center gap-2">
            <Button onClick={handleRetry} variant="outline" className="border-white/20" data-testid="import-retry-btn">
              <RefreshCcw className="w-4 h-4 mr-1" /> Reintentar
            </Button>
            <Button onClick={handleClose} variant="ghost">Cerrar</Button>
          </div>
        </div>
      );
    }

    const { imported = 0, updated = 0, failed = 0, skipped = 0, mode: rmode } = executeRes;
    const totalOk = (rmode === 'create' ? imported : updated);
    const action = rmode === 'create' ? 'creadas' : 'actualizadas';

    return (
      <div className="space-y-4 text-center" data-testid="import-step-5-success">
        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" />
        <div>
          <p className="text-lg font-semibold text-white">¡Listo!</p>
          <p className="text-sm text-muted-foreground">
            Se {totalOk === 1 ? 'importó' : 'importaron'} <strong className="text-emerald-300">{totalOk}</strong> propiedad{totalOk === 1 ? '' : 'es'} {action} correctamente.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 text-left">
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">{rmode === 'create' ? 'Creadas' : 'Actualizadas'}</p>
              <p className="text-xl font-bold text-emerald-300">{totalOk}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Saltadas</p>
              <p className="text-xl font-bold text-zinc-300">{skipped}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Fallidas</p>
              <p className={`text-xl font-bold ${failed > 0 ? 'text-red-300' : 'text-white'}`}>{failed}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-center gap-2 pt-2">
          <Button onClick={handleDownloadReport} variant="outline" className="border-white/20" data-testid="import-download-report-btn">
            <FileDown className="w-4 h-4 mr-1" /> Descargar reporte
          </Button>
          <Button onClick={handleFinishAndReload} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="import-finish-btn">
            Ver propiedades
          </Button>
        </div>
      </div>
    );
  };

  // ── Stepper indicator ──
  const stepLabels = ['Inicio', 'Subir', 'Revisar', 'Precios', 'Resumen', 'Resultado'];
  const renderStepper = () => (
    <div className="flex items-center gap-1 mb-3" data-testid="import-stepper">
      {stepLabels.map((label, idx) => (
        <div key={label} className="flex items-center gap-1 flex-1">
          <div
            className={`h-1 flex-1 rounded-full ${idx <= step ? 'bg-emerald-500' : 'bg-white/10'}`}
            data-testid={`stepper-bar-${idx}`}
          />
          <span className={`text-[9px] uppercase shrink-0 ${idx === step ? 'text-emerald-300 font-bold' : 'text-muted-foreground'}`}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card/95 backdrop-blur border-white/10" data-testid="import-properties-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
            Importar / Exportar propiedades
          </DialogTitle>
        </DialogHeader>

        {renderStepper()}

        {showLogs ? renderLogsPanel() : (
          <>
            {step === 0 && renderStep0()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
            {step === 5 && renderStep5()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportPropertiesModal;
